import { Icon, Modal } from '@brandbank/portal-components';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { IdShotAccess, IdShotItem } from '../connect-max.types';

export type IdShotSelectModalProps = {
  initialSelected: IdShotItem;
};

export default NiceModal.create(
  ({ initialSelected }: IdShotSelectModalProps) => {
    const { hide, remove, resolve, visible } = useModal();

    type Access = 'locked' | 'unlocked' | 'restricted';

    const messages: Record<Access, string> = {
      locked: 'The primary image cannot be replaced by the supplier user',
      unlocked:
        'The primary image can be replaced with any image, including supplier uploaded ones',
      restricted:
        'The primary image can be replaced but only for one of a nominated list of Brandbank shot types',
    };

    const [access, setAccess] = useState<IdShotAccess>(initialSelected.access);
    const [shotTypeString, setShotTypeString] = useState<string>(
      initialSelected.permittedShotTypes.join(',')
    );

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleAccessChange = (event: SelectChangeEvent) => {
      setAccess(event.target.value as IdShotAccess);
    };

    const handleShotTypesChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setErrorMessage('');
      const value = event.target.value;
      setShotTypeString(value);
    };

    const handleSubmit = (): void => {
      let shotTypes: number[] = [];
      let isValid = true;

      if (access === 'restricted') {
        const splitValues = shotTypeString
          .trim()
          .replace(/\s+/g, '')
          .split(',')
          .filter((v) => !!v);

        const duplicatedValues = Array.from(new Set(splitValues));

        duplicatedValues.forEach((value) => {
          const isOnlyNumbers = /[0-9]|\./.test(value); // should only allow numbers
          if (!isOnlyNumbers) {
            setErrorMessage('Please provide numeric values only');
            isValid = false;
          }
          shotTypes = duplicatedValues.map((v) => Number(v));
        });
      }

      if (isValid) {
        resolve({ access: access, permittedShotTypes: shotTypes });
        hide();
      }
    };

    return (
      <Modal
        action={
          <>
            <Button onClick={hide} variant='outlined'>
              Cancel
            </Button>
            <Button
              disabled={
                (shotTypeString.length === 0 && access === 'restricted') ||
                !access
              }
              onClick={handleSubmit}
              variant='contained'
            >
              Confirm
            </Button>
          </>
        }
        muiTransitionProps={{ onExited: remove }}
        onClose={hide}
        open={visible}
        title='Select Shot Types'
        width='30%'
      >
        <Stack spacing={1}>
          <FormControl fullWidth>
            <InputLabel id='access-select-label'>Access</InputLabel>
            <Select
              labelId='access-select-label'
              onChange={handleAccessChange}
              value={access}
            >
              <MenuItem value='locked'>Locked</MenuItem>
              <MenuItem value='restricted'>Restricted</MenuItem>
              <MenuItem value='unlocked'>Unlocked</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label='Permitted Shot Types'
            value={shotTypeString}
            onChange={handleShotTypesChange}
            disabled={!access || access === 'locked' || access === 'unlocked'}
            helperText={errorMessage}
            error={!!errorMessage}
            FormHelperTextProps={{
              sx: {
                margin: 0,
                paddingTop: '0.3em',
              },
            }}
          />

          {access === 'restricted' && (
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              color='warning.main'
            >
              <Icon iconName='info' fontSize='small' sx={{ ml: '0.1rem' }} />
              <Typography textAlign='right' fontSize='12px'>
                At least 1 Shot Type must be provided
              </Typography>
            </Stack>
          )}

          <Typography textAlign='left' fontSize='14px' sx={{ ml: '0.1rem' }}>
            {messages[access as Access]}
          </Typography>
        </Stack>
      </Modal>
    );
  }
);
